<template>
	<v-main>
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-toolbar-title>Report finanziario</v-toolbar-title>
						<v-btn v-if="tabValue === 'forecast'" class="ml-6" outlined color="primary" @click="$refs.forecast && $refs.forecast.startTour()">
							<v-icon left>mdi-school-outline</v-icon>Tutorial guidato
						</v-btn>
						<v-spacer></v-spacer>
						<v-toolbar-items>
							<v-btn text data-tour="export" :disabled="!report" title="Esporta in Excel" @click="esporta">
								<v-icon>mdi-arrow-collapse-down</v-icon>
							</v-btn>
							<v-menu offset-y left>
								<template v-slot:activator="{ on, attrs }">
									<v-btn text v-bind="attrs" title="Altre azioni" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item link :disabled="!canRecompute" @click="dialogRicalcola = true">
										<v-list-item-icon><v-icon small>mdi-camera-retake-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Ricalcola {{ singleMonth ? monthLabel(singleMonth.month) : 'mese' }}</v-list-item-title>
									</v-list-item>
									<v-list-item link to="/finance/costs">
										<v-list-item-icon><v-icon small>mdi-receipt-text-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Costi manuali</v-list-item-title>
									</v-list-item>
									<v-list-item link to="/finance/fees">
										<v-list-item-icon><v-icon small>mdi-tune-variant</v-icon></v-list-item-icon>
										<v-list-item-title>Commissioni</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-toolbar-items>
					</v-toolbar>

					<v-col cols="12">
						<FinancePeriodBar
							v-model="filtri"
							:platforms="channels"
							:loading="loading"
							:refreshing="refreshing"
							:refresh-progress="refreshProgress"
							:refresh-label="refreshLabel"
							@refresh="aggiorna"
						/>

						<div v-if="data" class="fin-status">
							<v-chip v-for="m in data.months" :key="m.month" small label outlined :color="status(m).color" :title="statusTitle(m)">
								<v-icon x-small left>{{ status(m).icon }}</v-icon>
								{{ monthLabel(m.month) }} · {{ status(m).label }}
							</v-chip>
							<span v-if="feesPending.length" class="fin-muted orange--text text--darken-2">Commissioni in lettura dalle API ({{ feesPending.join(', ') }}): il mese si aggiorna appena finisce.</span>
							<span v-if="warnings.approxCurrencyOrders" class="fin-muted">{{ warnings.approxCurrencyOrders }} ordini in valuta estera convertiti con il cambio attuale del profilo.</span>
							<span v-if="warnings.unconvertedOrders" class="fin-muted orange--text text--darken-2">{{ warnings.unconvertedOrders }} ordini in valuta estera senza cambio: sommati come euro.</span>
						</div>

						<v-tabs v-model="tab" class="mt-2 mb-3" background-color="transparent" data-tour="tabs">
							<v-tab v-for="t in TABS" :key="t.value">{{ t.label }}</v-tab>
						</v-tabs>

						<!-- la previsione guarda avanti: non dipende dal periodo scelto ne' dalla fotografia caricata -->
						<FinanceForecast v-if="tabValue === 'forecast'" ref="forecast" :platform="filtri.platform" @error="errore" />
						<div v-else-if="!report && loading" class="fin-empty">
							<v-progress-circular indeterminate color="primary" />
							<div class="fin-muted mt-3">Calcolo in corso: la prima apertura di un mese può richiedere qualche secondo, poi resta salvata.</div>
						</div>
						<template v-else-if="report">
							<FinanceSummary
								v-if="tabValue === 'summary'"
								:report="report"
								:previous="previous"
								:current-label="currentLabel"
								:previous-label="previousLabel"
								@open-fees="$router.push({ path: '/finance/fees', query: singleMonth ? { month: singleMonth.month } : {} })"
							/>
							<FinanceProfit v-else-if="tabValue === 'profit'" :report="report" />
							<FinancePnl
								v-else-if="tabValue === 'pnl'"
								:report="report"
								:previous="previous"
								:current-label="currentLabel"
								:previous-label="previousLabel"
								:platform="filtri.platform"
								@open-costs="$router.push('/finance/costs')"
								@open-fees="$router.push({ path: '/finance/fees', query: singleMonth ? { month: singleMonth.month } : {} })"
							/>
							<FinanceVat v-else :report="report" />
						</template>
					</v-col>
				</v-card>
			</v-layout>
		</v-container>

		<v-dialog v-model="dialogRicalcola" max-width="480">
			<v-card>
				<v-card-title class="text-h6">Ricalcolare {{ singleMonth ? monthLabel(singleMonth.month) : '' }}?</v-card-title>
				<v-card-text>
					La fotografia del mese viene rifatta con i dati di oggi: ordini, resi, costi, commissioni e acquisti caricati dopo la chiusura entrano nel conto. La versione precedente resta salvata nello
					storico del mese.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="dialogRicalcola = false">Annulla</v-btn>
					<v-btn depressed color="primary" :loading="ricalcolo" @click="ricalcola">Ricalcola</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-main>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import FinancePeriodBar from '@/components/finance/FinancePeriodBar.vue';
import FinanceSummary from '@/components/finance/FinanceSummary.vue';
import FinanceProfit from '@/components/finance/FinanceProfit.vue';
import FinancePnl from '@/components/finance/FinancePnl.vue';
import FinanceVat from '@/components/finance/FinanceVat.vue';
import { refreshMixin } from '@/components/finance/refreshMixin';
import { periodMonths, previousMonth, monthLabel, STATUS } from '@/components/finance/format';

const FinanceForecast = () => import('@/components/finance/forecast/FinanceForecast.vue');

const TABS = [
	{ value: 'summary', label: 'Riepilogo' },
	{ value: 'profit', label: 'Profitto' },
	{ value: 'pnl', label: 'Conto economico' },
	{ value: 'vat', label: 'IVA' },
	{ value: 'forecast', label: 'Previsioni' },
];

// filtri della barra <-> query dell'URL (un refresh o un link riaprono lo stesso periodo)
const fromQuery = query => {
	const [py, pm] = previousMonth().split('-').map(Number);
	const period = ['month', 'quarter', 'year'].includes(query.period) ? query.period : 'month';
	return {
		period,
		year: +query.year || py,
		month: +query.month || pm,
		quarter: +query.quarter || Math.ceil(pm / 3),
		compare: ['prev', 'yoy'].includes(query.compare) ? query.compare : '',
		platform: query.platform || '',
	};
};

export default {
	name: 'FinanceReport',
	// "Aggiorna" ricalcola i mesi del periodo, anche quelli chiusi
	mixins: [refreshMixin('finance')],
	components: { FinancePeriodBar, FinanceSummary, FinanceProfit, FinancePnl, FinanceVat, FinanceForecast },
	data() {
		const tab = Math.max(
			0,
			TABS.findIndex(t => t.value === this.$route.query.tab),
		);
		return {
			TABS,
			tab,
			filtri: fromQuery(this.$route.query),
			data: null,
			loading: false,
			dialogRicalcola: false,
			ricalcolo: false,
			channels: [],
		};
	},
	computed: {
		tabValue() {
			return (TABS[this.tab] || TABS[0]).value;
		},
		report() {
			return this.data && this.data.report;
		},
		previous() {
			return this.data && this.data.previous;
		},
		// fonti API non ancora lette per i mesi del periodo
		feesPending() {
			return (this.data && this.data.feesPending) || [];
		},
		warnings() {
			return (this.report && this.report.warnings) || {};
		},
		range() {
			return periodMonths(this.filtri);
		},
		currentLabel() {
			return this.rangeLabel(this.range.from, this.range.to);
		},
		previousLabel() {
			const months = this.data && this.data.previousMonths;
			return months && months.length ? this.rangeLabel(months[0], months[months.length - 1]) : '';
		},
		singleMonth() {
			return this.data && this.data.months.length === 1 ? this.data.months[0] : null;
		},
		canRecompute() {
			return !!this.singleMonth && this.singleMonth.status !== 'open';
		},
	},
	watch: {
		filtri: {
			deep: true,
			handler() {
				this.scriviUrl();
				this.carica();
			},
		},
		tab() {
			this.scriviUrl();
		},
		// avanti/indietro del browser o link con un'altra scheda: la pagina resta la stessa, cambia solo la query
		'$route.query.tab'(value) {
			const index = Math.max(
				0,
				TABS.findIndex(t => t.value === value),
			);
			if (index !== this.tab) this.tab = index;
		},
	},
	mounted() {
		this.carica();
	},
	beforeDestroy() {
		this.destroyData();
	},
	methods: {
		...mapMutations({ setLogout: 'profile/setLogout' }),
		...mapActions({ fetchReport: 'finance/fetchReport', recompute: 'finance/recompute', destroyData: 'finance/destroyData' }),
		monthLabel,
		status(m) {
			return STATUS[m.status] || STATUS.closed;
		},
		statusTitle(m) {
			if (m.status === 'open') return 'Mese in corso: calcolato al momento, non viene salvato';
			const when = m.computedAt ? this.$moment(m.computedAt).format('DD/MM/YYYY HH:mm') : '';
			if (m.status === 'provisional') return `Salvato il ${when}: verrà ricalcolato dal giorno 3 del mese successivo, quando arrivano i dati di fine mese`;
			return `Fotografia del ${when}`;
		},
		rangeLabel(from, to) {
			if (!from) return '';
			if (from === to) return monthLabel(from);
			if (from.slice(0, 4) === to.slice(0, 4) && from.endsWith('-01') && to.endsWith('-12')) return from.slice(0, 4);
			return `${monthLabel(from)} – ${monthLabel(to)}`;
		},
		scriviUrl() {
			const f = this.filtri;
			const query = { period: f.period, year: String(f.year) };
			if (f.period === 'month') query.month = String(f.month);
			if (f.period === 'quarter') query.quarter = String(f.quarter);
			if (f.compare) query.compare = f.compare;
			if (f.platform) query.platform = f.platform;
			if (this.tab) query.tab = this.tabValue;
			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {});
		},
		async carica() {
			const { from, to } = this.range;
			this.loading = true;
			try {
				this.data = await this.fetchReport({ from, to, platform: this.filtri.platform, compare: this.filtri.compare });
				if (!this.filtri.platform) this.channels = this.data.channels || [];
			} catch (err) {
				this.errore(err);
			} finally {
				this.loading = false;
			}
		},
		async ricalcola() {
			this.ricalcolo = true;
			try {
				await this.recompute(this.singleMonth.month);
				this.dialogRicalcola = false;
				this.notify('success', `${monthLabel(this.singleMonth.month)} ricalcolato`);
				await this.carica();
			} catch (err) {
				this.errore(err);
			} finally {
				this.ricalcolo = false;
			}
		},
		esporta() {
			const { from, to } = this.range;
			this.exportAsync('finance', 'excel', { from, to, platform: this.filtri.platform || undefined });
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 5000 });
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			const errors = res && res.data && res.data.error;
			this.notify('error', Array.isArray(errors) ? errors.map(e => e.msg).join(', ') : (res && res.data && res.data.message) || err.message);
		},
	},
};
</script>

<style scoped>
.fin-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.fin-status {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 10px;
	margin-top: 10px;
}
.fin-empty {
	text-align: center;
	padding: 60px 0;
}
</style>
